// @flow
import zentakeApi from 'api'
import React from 'react'
import { toast } from 'react-toastify'
import type { PacketResponse } from 'types/packet-response'
import type { Response } from 'types/response'
import { ZentakeContext } from 'utils/ZentakeContext'

function useResponseArchive(
  response: ?Response,
  setResponse: ?(Response) => void,
  packetResponse: ?PacketResponse,
  setPacketResponse: ?(PacketResponse) => void
): [
  boolean,
  { title: string, onClick: () => Promise<void>, loading: boolean },
] {
  const { setAlertConfig } = React.useContext(ZentakeContext)

  const isArchived = Boolean(
    (!response?.packet_response && response?.is_archived) ||
      packetResponse?.is_archived
  )

  const [isArchiving, setIsArchiving] = React.useState(false)

  const archiveResponse = React.useCallback(async () => {
    if (!response || !setResponse) return

    await zentakeApi.archiveResponse(response?.uid || response?.id)
    setResponse({ ...response, is_archived: true })
  }, [response, setResponse])

  const archivePacketResponse = React.useCallback(async () => {
    if (!packetResponse || !setPacketResponse) return null

    await zentakeApi.archivePacketResponse(packetResponse.id)
    setPacketResponse({ ...packetResponse, is_archived: true })
  }, [packetResponse, setPacketResponse])

  const archive = React.useCallback(async () => {
    setIsArchiving(true)

    if (response) {
      await archiveResponse()
    } else if (packetResponse) {
      await archivePacketResponse()
    }

    toast.success('Response archived')

    setIsArchiving(false)
  }, [archivePacketResponse, archiveResponse, packetResponse, response])

  const onArchive = React.useCallback(async () => {
    setAlertConfig({
      title: 'Archive form response',
      description: 'Are you sure you want to archive this response?',
      buttons: [
        { title: 'Cancel' },
        { title: 'Archive', style: 'destructive', onPress: archive },
      ],
      canDismiss: false,
    })
  }, [setAlertConfig, archive])

  return [
    isArchived,
    {
      title: 'Archive',
      onClick: onArchive,
      loading: isArchiving,
    },
  ]
}

export default useResponseArchive
